import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { tasks } from "../../lib/api";
import type { SpawnedProposal, TaskPriority } from "../../lib/types";
import { Button } from "../shared/Button";
import { cn } from "../../lib/cn";
import { PriorityBadge } from "./TaskCard";

/**
 * Right-pane card that surfaces follow-up tasks the agent, the independent
 * review or a research run proposed. Each proposal went through the proposal
 * gate: it carries a kind, its evidence and the existing tasks it resembles,
 * and it is parked as an idea when it may not become a task on its own (no
 * evidence, or new scope). The operator decides one proposal at a time:
 * approve materializes it as a backlog row under this task, later parks it,
 * reject drops it. There is no "approve all": that is how boards bloat.
 */
export function SpawnedProposalsCard({
  taskId,
  proposals,
}: {
  taskId: string;
  proposals: SpawnedProposal[];
}) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  if (!proposals.length) return null;
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["task", taskId] });
    qc.invalidateQueries({ queryKey: ["kanban"] });
    qc.invalidateQueries({ queryKey: ["tasks"] });
    qc.invalidateQueries({ queryKey: ["ideas"] });
  };
  const run = async (action: () => Promise<unknown>) => {
    if (busy) return;
    setBusy(true);
    try {
      await action();
      refresh();
    } finally {
      setBusy(false);
    }
  };
  const open = proposals.map((p, i) => ({ p, i })).filter(({ p }) => !p.parked);
  const parked = proposals.map((p, i) => ({ p, i })).filter(({ p }) => p.parked);

  return (
    <div className="mx-4 my-3 rounded-md border border-accent/40 bg-accent-soft/40">
      <div className="px-3 py-2 border-b border-border-subtle flex items-center gap-2">
        <span className="text-small font-medium text-text-primary">
          Follow-ups proposed
        </span>
        <span className="text-mono text-[11.5px] text-text-tertiary">
          {open.length} waiting for your OK · {parked.length} parked
        </span>
        <div className="flex-1" />
        <Button
          size="sm"
          variant="secondary"
          disabled={busy}
          onClick={() =>
            run(() =>
              tasks.rejectSpawned(
                taskId,
                proposals.map((_, i) => i),
              ),
            )
          }
        >
          Reject all
        </Button>
      </div>
      <div className="divide-y divide-border-subtle">
        {open.map(({ p, i }) => (
          <ProposalRow key={i} index={i} proposal={p} taskId={taskId} onChanged={refresh} />
        ))}
        {parked.length > 0 && (
          <div className="px-3 py-1.5 text-caption text-text-tertiary bg-bg-inset/40">
            Parked ideas: they never become tasks unless you approve one.
          </div>
        )}
        {parked.map(({ p, i }) => (
          <ProposalRow key={i} index={i} proposal={p} taskId={taskId} onChanged={refresh} />
        ))}
      </div>
    </div>
  );
}

export function ProposalFacts({ proposal }: { proposal: SpawnedProposal }) {
  const similar = (proposal.similar ?? []).slice(0, 2);
  return (
    <>
      <div className="text-caption text-text-tertiary mt-1 flex flex-wrap gap-x-2">
        <span className="text-mono uppercase">{proposal.kind ?? "feature"}</span>
        <span>{proposal.evidence ? `evidence: ${proposal.evidence}` : "no evidence"}</span>
        {proposal.source && <span>from the {proposal.source}</span>}
      </div>
      {proposal.reason && (
        <p className="text-caption text-text-tertiary mt-0.5 italic">{proposal.reason}</p>
      )}
      {similar.length > 0 && (
        <p className="text-caption text-text-secondary mt-0.5">
          Looks like:{" "}
          {similar.map((s, n) => (
            <span key={s.task_id}>
              {n > 0 && "; "}
              "{s.title}" ({s.status})
            </span>
          ))}
        </p>
      )}
      {proposal.parked && (
        <p className="text-caption mt-0.5" style={{ color: "var(--status-paused)" }}>
          Parked: {proposal.parked_why ?? "waiting for your OK"}
        </p>
      )}
    </>
  );
}

function ProposalRow({
  index,
  proposal,
  taskId,
  onChanged,
}: {
  index: number;
  proposal: SpawnedProposal;
  taskId: string;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState<SpawnedProposal>(proposal);
  const run = async (action: () => Promise<unknown>) => {
    if (busy) return;
    setBusy(true);
    try {
      await action();
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <div className="px-3 py-2 space-y-2">
        <input
          value={draft.title}
          onChange={(e) => setDraft({ ...draft, title: e.target.value })}
          placeholder="Title"
          className={cn(
            "w-full h-8 px-2 rounded-sm bg-bg-canvas border border-border-subtle",
            "text-small text-text-primary outline-none focus-visible:ring-2 focus-visible:ring-accent",
          )}
        />
        <textarea
          value={draft.description ?? ""}
          onChange={(e) => setDraft({ ...draft, description: e.target.value })}
          placeholder="Description (optional)"
          rows={2}
          className={cn(
            "w-full px-2 py-1.5 rounded-sm bg-bg-canvas border border-border-subtle",
            "text-small text-text-primary outline-none focus-visible:ring-2 focus-visible:ring-accent",
          )}
        />
        <textarea
          value={draft.evidence ?? ""}
          onChange={(e) => setDraft({ ...draft, evidence: e.target.value })}
          placeholder="Evidence: the failing test, log line, error or request that shows it is real"
          rows={2}
          className={cn(
            "w-full px-2 py-1.5 rounded-sm bg-bg-canvas border border-border-subtle",
            "text-small text-text-primary outline-none focus-visible:ring-2 focus-visible:ring-accent",
          )}
        />
        <textarea
          value={draft.prompt ?? ""}
          onChange={(e) => setDraft({ ...draft, prompt: e.target.value })}
          placeholder="Prompt (optional — falls back to suggester)"
          rows={3}
          className={cn(
            "w-full px-2 py-1.5 rounded-sm bg-bg-canvas border border-border-subtle",
            "text-mono text-[12px] text-text-primary outline-none focus-visible:ring-2 focus-visible:ring-accent",
          )}
        />
        <div className="flex items-center gap-2">
          <select
            value={draft.kind ?? "feature"}
            onChange={(e) => setDraft({ ...draft, kind: e.target.value })}
            className="h-7 px-2 rounded-sm bg-bg-canvas border border-border-subtle text-mono text-[11.5px] text-text-secondary"
          >
            {["bug", "test", "docs", "cleanup", "feature", "hardening"].map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
          <select
            value={draft.priority ?? ""}
            onChange={(e) =>
              setDraft({
                ...draft,
                priority: (e.target.value || null) as TaskPriority | null,
              })
            }
            className="h-7 px-2 rounded-sm bg-bg-canvas border border-border-subtle text-mono text-[11.5px] text-text-secondary"
          >
            <option value="">priority —</option>
            <option value="P0">P0</option>
            <option value="P1">P1</option>
            <option value="P2">P2</option>
            <option value="P3">P3</option>
          </select>
          <div className="flex-1" />
          <Button
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={() => setEditing(false)}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            variant="primary"
            disabled={busy}
            onClick={() => run(() => tasks.approveSpawned(taskId, [draft], [index]))}
          >
            Approve
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-3 py-2 flex items-start gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          {proposal.priority && <PriorityBadge priority={proposal.priority} />}
          <span className="text-small text-text-primary font-medium truncate">
            {proposal.title}
          </span>
        </div>
        {proposal.description && (
          <p className="text-small text-text-secondary mt-1 whitespace-pre-wrap">
            {proposal.description}
          </p>
        )}
        <ProposalFacts proposal={proposal} />
        {proposal.prompt && (
          <pre className="mt-2 px-2 py-1.5 rounded-sm bg-bg-inset text-mono text-[11.5px] text-text-secondary whitespace-pre-wrap break-all max-h-32 overflow-y-auto">
            {proposal.prompt}
          </pre>
        )}
      </div>
      <div className="flex flex-col items-stretch gap-1">
        <Button size="sm" variant="ghost" disabled={busy} onClick={() => setEditing(true)}>
          Edit
        </Button>
        {!proposal.parked && (
          <Button
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={() => run(() => tasks.parkSpawned(taskId, [index]))}
          >
            Later
          </Button>
        )}
        <Button
          size="sm"
          variant="ghost"
          disabled={busy}
          onClick={() => run(() => tasks.rejectSpawned(taskId, [index]))}
        >
          Reject
        </Button>
        <Button
          size="sm"
          variant="primary"
          disabled={busy}
          onClick={() => run(() => tasks.approveSpawned(taskId, [proposal], [index]))}
        >
          {proposal.parked ? "Approve anyway" : "Approve"}
        </Button>
      </div>
    </div>
  );
}
